import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { useGetCountries } from "@/api/country/useCountryAPI";
import {
  useGetDataProviders,
  useGetDatasetDataMutation,
  useGetDatasetListQuery,
  useGetDatasetMetadataQuery
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import { EXPLORE_DATASET_PRE_SELECTION_STEP_IDS } from "@/components/onboarding/configs/explore-dataset";
import Modal from "@/components/ui/modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { COUNTRY_DATA } from "@/constants/dataset";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { useExploreDatasetMocks } from "@/hooks/onboarding/explorer/useExploreDatasetMocks";
import useSessionStorage from "@/hooks/store/useSessionStorage";
import useLoadingAndToast from "@/hooks/ui/useLoadingAndToast";
import { EToastType, showToast } from "@/hooks/ui/useToast";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDebounce } from "@/hooks/useDebounce";
import { usePagination } from "@/hooks/usePagination";
import type { RootState } from "@/store";
import { setSelectedDatasetExplorer } from "@/store/slice/datasetSlice";
import type {
  CountryCode,
  Source,
  SpatialAggregationType
} from "@/types/common";
import type {
  DatasetExploreItem,
  DatasetMetadata,
  License
} from "@/types/dataset";

import {
  type DatasetExplorerSessionStorageData,
  DEFAULT_SESSION_DATA,
  SESSION_STORAGE_KEY
} from "./constants";
import { DataExploreTab } from "./DataExploreTab";
import { POIParameterTab } from "./POIParameterTab";
import { Preview } from "./Preview";

// Re-exported for backward compatibility — these used to live in this file and
// are imported from here by sibling components/tests. New code should import
// directly from "./constants".
export type { DatasetExplorerSessionStorageData } from "./constants";
export { DEFAULT_SESSION_DATA, SESSION_STORAGE_KEY } from "./constants";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const DatasetExplorerPopup = ({ isOpen, onClose }: Props) => {
  const {
    sessionValue: sessionStorageData,
    setSessionItem,
    removeSessionItem
  } = useSessionStorage<DatasetExplorerSessionStorageData>(
    SESSION_STORAGE_KEY,
    DEFAULT_SESSION_DATA
  );
  const dispatch = useDispatch();
  const location = useLocation();
  const { datasetExplorerState, poiParameterState } = location?.state ?? {};
  const {
    isUseNewDatasetExplorerModal,
    isMultipleSearchTableDataset,
    isPOIParameterEnabled,
    isBVTDataForCatchmentEnabled,
    isParameterPOIForCatchmentEnabled
  } = useAppFeatures();
  const { showCatchmentPopup } = useSelector((state: RootState) => state.ui);
  const { catchmentSettingsTemp } = useSelector(
    (state: RootState) => state.spatial
  );
  const onboardingStepId = useSelector(
    (state: RootState) => state.onboarding.activeStepTourId
  );

  const isOnboarding = useSelector(
    (state: RootState) => state.onboarding.active
  );
  const mocksOnboarding = useExploreDatasetMocks();

  const shouldResetOnboardingSelection =
    isOnboarding &&
    EXPLORE_DATASET_PRE_SELECTION_STEP_IDS.includes(onboardingStepId ?? "");

  const {
    pageSize,
    setPageSize: originalSetPageSize,
    currentPage,
    setCurrentPage: originalSetCurrentPage
  } = usePagination({
    initialPageSize:
      sessionStorageData?.pageSize ?? DEFAULT_SESSION_DATA.pageSize,
    initialPage:
      sessionStorageData?.currentPage ?? DEFAULT_SESSION_DATA.currentPage
  });

  const { showError } = useLoadingAndToast();

  const { data: dataProviders } = useGetDataProviders({
    enabled: isUseNewDatasetExplorerModal,
    meta: {
      isOnboarding: isOnboarding,
      onboardingMockData: mocksOnboarding.dataProviders
    }
  });

  const [dataSource, setDataSource] = useState<Source>(
    // Catchment popup default by flag: ON → "bvt" (Bvarta & Partner Data),
    // OFF → "user" (My Organization) to match the legacy flow. Outside the
    // catchment popup the session value (or "bvt" default) applies as before.
    showCatchmentPopup
      ? isBVTDataForCatchmentEnabled
        ? "bvt"
        : "user"
      : (sessionStorageData?.dataSource ?? DEFAULT_SESSION_DATA.dataSource)
  );
  const [spatialAgg, setSpatialAgg] = useState<SpatialAggregationType | "all">(
    () => {
      // Catchment popup: lock to the first added dataset's spatial aggregation
      // type if any, otherwise default to "poi". The lazy initializer runs
      // only on mount — the modal unmounts on close, so reopening picks up
      // the latest catchmentSettingsTemp value.
      if (showCatchmentPopup) {
        if (isBVTDataForCatchmentEnabled) {
          const datasetConfig = catchmentSettingsTemp?.datasetConfig;
          const firstId = datasetConfig?.visualizationOrder?.[0];
          const lockedType = firstId
            ? datasetConfig?.datasets?.[firstId]?.dataset?.data
                ?.spatial_aggregation_type
            : undefined;
          if (lockedType) return lockedType;
        }
        return "poi";
      }
      return (
        sessionStorageData?.spatialAggregationType ??
        DEFAULT_SESSION_DATA.spatialAggregationType
      );
    }
  );

  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(
    sessionStorageData?.providerId
      ? sessionStorageData.providerId.split(",")
      : []
  );
  const [license, setLicense] = useState<License | "all">(
    sessionStorageData?.license ?? DEFAULT_SESSION_DATA.license
  );
  const [isButtonPreviewEnabled, setIsButtonPreviewEnabled] =
    useState<boolean>(false);
  const [isPreview, setIsPreview] = useState<boolean>(false);

  const [selectedCountry, setSelectedCountry] = useState<{
    label: string;
    value: CountryCode;
  }>({
    label: sessionStorageData?.country
      ? COUNTRY_DATA.find(
          (country) => country.value === sessionStorageData.country
        )?.label || "All"
      : "All",
    value: sessionStorageData?.country ?? DEFAULT_SESSION_DATA.country
  });
  const [selectedDataset, setSelectedDataset] =
    useState<DatasetExploreItem | null>(null);
  const activeSelectedDataset = shouldResetOnboardingSelection
    ? null
    : selectedDataset;
  const [searchQuery, setSearchQuery] = useState<string>(
    sessionStorageData?.searchQuery ?? DEFAULT_SESSION_DATA.searchQuery
  );
  const [activeTab, setActiveTab] = useState<string>("data-explore");

  const debouncedSearchQuery = useDebounce(searchQuery, 1000);
  const { data: selectedDatasetMetadata } = useGetDatasetMetadataQuery(
    activeSelectedDataset,
    {
      meta: {
        isOnboarding: isOnboarding,
        onboardingMockData: mocksOnboarding.datasetDetail
      }
    }
  );
  const { data: userProfileData } = useGetUserProfileAPI();

  const {
    mutateAsync: getDatasetDataPost,
    isPending: isLoadingDatasetData,
    data: datasetData
  } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone,
    options: {
      onError: () => {
        showError(
          <NotificationContent
            title="Something Went Wrong"
            description="We’re sorry, we cannot proceed with your request. Please try again."
          />
        );
      }
    }
  });

  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries({
      options: {
        meta: {
          isOnboarding: isOnboarding,
          onboardingMockData: mocksOnboarding.countries
        }
      }
    });

  const countryOptions =
    countryData?.map((country) => ({
      label: country.name.toLowerCase() === "global" ? "All" : country.name,
      value: country.id === "global" ? "" : country.id // "global" is represented as empty string in API
    })) || [];

  const {
    data: datasetList,
    isLoading: isLoadingDatasetList,
    error: datasetsListError
  } = useGetDatasetListQuery(
    {
      q: debouncedSearchQuery?.length >= 2 ? debouncedSearchQuery : "", // Allow search when keyword size >=2
      country: selectedCountry.value,
      source: dataSource || undefined,
      spatial_aggregation_type: spatialAgg || "all",
      published: true,
      page: currentPage,
      per_page: pageSize,
      ...(isUseNewDatasetExplorerModal && dataSource === "bvt"
        ? {
            license: license === "all" ? "regular,premium" : license,
            provider_id:
              selectedDataSources.length > 0
                ? selectedDataSources.join(",")
                : undefined
          }
        : {})
    },
    {
      meta: {
        ...(isOnboarding
          ? {
              isOnboarding: isOnboarding,
              onboardingMockData: mocksOnboarding.datasetList
            }
          : {})
      },
      ...(isUseNewDatasetExplorerModal
        ? { gcTime: 0, staleTime: 0, refetchOnMount: true }
        : {})
    }
  );

  useEffect(() => {
    if (datasetsListError) {
      showToast({
        message: getDisplayErrorMessage(datasetsListError),
        type: EToastType.ERROR
      });
    }
  }, [datasetsListError]);

  const handleSelectData = async (data: DatasetExploreItem) => {
    if (data.id === selectedDataset?.id) return;
    setSelectedDataset(data);
    setIsButtonPreviewEnabled(true);

    if (showCatchmentPopup && !isBVTDataForCatchmentEnabled) {
      await getDatasetDataPost({
        id: data.id
      });
    }
  };

  useEffect(() => {
    if (datasetExplorerState) {
      setSelectedDataset(datasetExplorerState);
      setIsPreview(true);
    }
  }, [datasetExplorerState]);

  useEffect(() => {
    if (poiParameterState) {
      setActiveTab("poi-parameter");
    }
  }, [poiParameterState]);

  // Keyed on the boolean only (not selectedDataset): a card click made while on
  // the selection step sets the selection without re-triggering this effect, so
  // the reset fires exactly when the tour returns to a pre-selection step.
  useEffect(() => {
    if (!shouldResetOnboardingSelection) return;
    setSelectedDataset(null);
    setIsButtonPreviewEnabled(false);
  }, [shouldResetOnboardingSelection]);

  const dataSourceProviders = (dataProviders ?? [])?.map((ctx) => ({
    id: ctx.id,
    label: ctx.name,
    icon: <img src={ctx.logo_asset_url} alt={ctx.name} className="w-6 h-6" />
  }));

  const isPreviewEnabledForCurrentStep =
    !shouldResetOnboardingSelection && isButtonPreviewEnabled;

  const handleChange =
    <T,>(setter: (value: T) => void) =>
    (e: T) => {
      setter(e);
      setSelectedDataset(null);
      setIsButtonPreviewEnabled(false);
    };

  const setPageSize = useCallback(
    (value: number) => {
      originalSetPageSize(value);
      setSessionItem({
        ...sessionStorageData,
        pageSize: value
      });
      setSelectedDataset(null);
      setIsButtonPreviewEnabled(false);
    },
    [originalSetPageSize, setSessionItem, sessionStorageData]
  );

  const setCurrentPage = useCallback(
    (value: number) => {
      originalSetCurrentPage(value);
      setSessionItem({
        ...sessionStorageData,
        currentPage: value
      });
    },
    [sessionStorageData, originalSetCurrentPage, setSessionItem]
  );

  const setCountry = useCallback(
    ({ label, value }: { label: string; value: CountryCode }) => {
      setSelectedCountry({
        label: label,
        value: value
      });
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        country: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, sessionStorageData, setSessionItem]
  );

  const setSearchDataset = useCallback(
    (value: string) => {
      setSearchQuery(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        searchQuery: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, sessionStorageData, setSessionItem]
  );

  const setSelectLicense = useCallback(
    (value: License | "all") => {
      setLicense(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        license: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, sessionStorageData, setSessionItem]
  );

  const setSelectDataSource = useCallback(
    (value: Source) => {
      setDataSource(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        dataSource: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, sessionStorageData, setSessionItem]
  );

  const setSpatialAggSession = useCallback(
    (value: SpatialAggregationType | "all") => {
      setSpatialAgg(value);
      originalSetCurrentPage(1);
      setSessionItem({
        ...sessionStorageData,
        spatialAggregationType: value,
        currentPage: 1
      });
    },
    [originalSetCurrentPage, sessionStorageData, setSessionItem]
  );

  const handleClose = () => {
    removeSessionItem();
    onClose();
    setIsPreview(false);
    location.state = null;
    dispatch(setSelectedDatasetExplorer(null));
  };

  // Tab visibility (Data Explorer + POI Parameter). Inside the catchment popup
  // it is gated solely by `isParameterPOIForCatchmentEnabled`; outside it uses
  // the general `isPOIParameterEnabled` flag.
  const showExplorerTabs = showCatchmentPopup
    ? isParameterPOIForCatchmentEnabled
    : isPOIParameterEnabled;

  const lockedCatchmentSpatialAgg = useMemo<
    SpatialAggregationType | undefined
  >(() => {
    // Multi-dataset catchment lock only applies when BVT flag is ON. In the
    // legacy flow there's only ever one POI dataset so no lock is needed.
    if (!isBVTDataForCatchmentEnabled) return undefined;
    // Lock only applies inside the catchment popup flow. Opening the
    // Dataset Explorer from the main menu must not inherit the catchment
    // dataset's spatial agg type, otherwise stale catchmentSettingsTemp
    // from a previous session would keep options disabled.
    if (!showCatchmentPopup) return undefined;
    const datasetConfig = catchmentSettingsTemp?.datasetConfig;
    const firstId = datasetConfig?.visualizationOrder?.[0];
    if (!firstId) return undefined;
    return datasetConfig?.datasets?.[firstId]?.dataset?.data
      ?.spatial_aggregation_type;
  }, [isBVTDataForCatchmentEnabled, catchmentSettingsTemp, showCatchmentPopup]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      containerClassname="w-[95vw] h-[95vh] flex flex-col"
      shouldCloseModalOnClickOutside={false}
    >
      {!isPreview ? (
        <>
          {/* Header */}
          <div className="flex flex-row justify-between p-6 pb-4">
            <h2 className="text-txtHeading font-bold text-[20px]">
              Dataset Explorer
            </h2>
            <button
              type="button"
              className="bg-transparent border-0"
              onClick={handleClose}
              aria-label="Close Dataset Explorer Modal"
            >
              <CloseIcon className="fill-dim-gray" />
            </button>
          </div>
          {showExplorerTabs ? (
            <Tabs
              value={activeTab}
              className="flex flex-col flex-1 overflow-hidden"
              onValueChange={setActiveTab}
            >
              <div className="px-6">
                <TabsList
                  className="justify-start h-12 p-0 bg-transparent border-b rounded-none border-gainsboro w-fit"
                  data-tourid="dataset-explorer-panel"
                >
                  <TabsTrigger
                    value="data-explore"
                    className="h-full rounded-none border-b-2 border-transparent px-4 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500 data-[state=active]:shadow-none data-[state=active]:font-semibold"
                  >
                    Data Explorer
                  </TabsTrigger>
                  <TabsTrigger
                    value="poi-parameter"
                    className="h-full rounded-none border-b-2 border-transparent px-4 data-[state=active]:border-teal-500 data-[state=active]:text-teal-500 data-[state=active]:shadow-none data-[state=active]:font-semibold"
                    disabled={lockedCatchmentSpatialAgg === "polygon"}
                  >
                    POI Parameter
                  </TabsTrigger>
                </TabsList>
              </div>
              {/* Content */}
              <TabsContent
                value="data-explore"
                className="flex flex-1 gap-5 p-6 pt-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <DataExploreTab
                  searchQuery={searchQuery}
                  setSearchDataset={setSearchDataset}
                  isLoadingDatasetList={isLoadingDatasetList}
                  isLoadingCountryData={isLoadingCountryData}
                  datasetList={datasetList}
                  debouncedSearchQuery={debouncedSearchQuery}
                  selectedDataset={activeSelectedDataset}
                  handleSelectData={handleSelectData}
                  isUseNewDatasetExplorerModal={isUseNewDatasetExplorerModal}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  currentPage={currentPage}
                  setCurrentPage={handleChange(setCurrentPage)}
                  isButtonPreviewEnabled={isPreviewEnabledForCurrentStep}
                  setIsPreview={setIsPreview}
                  countryOptions={countryOptions}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={handleChange(setCountry)}
                  dataSource={dataSource}
                  setDataSource={handleChange(setSelectDataSource)}
                  spatialAgg={spatialAgg}
                  setSpatialAgg={handleChange(setSpatialAggSession)}
                  license={license}
                  setLicense={handleChange(setSelectLicense)}
                  selectedDataSources={selectedDataSources}
                  setSelectedDataSources={handleChange<string[]>(
                    setSelectedDataSources
                  )}
                  dataSourceProviders={dataSourceProviders}
                  showCatchmentPopup={showCatchmentPopup ?? false}
                  sessionStorageData={sessionStorageData}
                  setSessionItem={setSessionItem}
                  selectedDatasetMetadata={
                    selectedDatasetMetadata as unknown as DatasetMetadata
                  }
                  onClose={handleClose}
                  isLoadingDatasetData={isLoadingDatasetData ?? false}
                  datasetData={datasetData}
                  lockedCatchmentSpatialAgg={lockedCatchmentSpatialAgg}
                />
              </TabsContent>
              <TabsContent
                value="poi-parameter"
                className="flex flex-1 gap-5 p-6 pt-0 overflow-hidden mt-0 data-[state=inactive]:hidden"
              >
                <POIParameterTab />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="flex flex-col flex-1 overflow-hidden">
              <DataExploreTab
                searchQuery={searchQuery}
                setSearchDataset={setSearchDataset}
                isLoadingDatasetList={isLoadingDatasetList}
                isLoadingCountryData={isLoadingCountryData}
                datasetList={datasetList}
                debouncedSearchQuery={debouncedSearchQuery}
                selectedDataset={activeSelectedDataset}
                handleSelectData={handleSelectData}
                isUseNewDatasetExplorerModal={isUseNewDatasetExplorerModal}
                pageSize={pageSize}
                setPageSize={setPageSize}
                currentPage={currentPage}
                setCurrentPage={handleChange(setCurrentPage)}
                isButtonPreviewEnabled={isPreviewEnabledForCurrentStep}
                setIsPreview={setIsPreview}
                countryOptions={countryOptions}
                selectedCountry={selectedCountry}
                setSelectedCountry={handleChange(setCountry)}
                dataSource={dataSource}
                setDataSource={handleChange(setSelectDataSource)}
                spatialAgg={spatialAgg}
                setSpatialAgg={handleChange(setSpatialAggSession)}
                license={license}
                setLicense={handleChange(setSelectLicense)}
                selectedDataSources={selectedDataSources}
                setSelectedDataSources={handleChange<string[]>(
                  setSelectedDataSources
                )}
                dataSourceProviders={dataSourceProviders}
                showCatchmentPopup={showCatchmentPopup ?? false}
                sessionStorageData={sessionStorageData}
                setSessionItem={setSessionItem}
                selectedDatasetMetadata={
                  selectedDatasetMetadata as unknown as DatasetMetadata
                }
                onClose={handleClose}
                isLoadingDatasetData={isLoadingDatasetData ?? false}
                datasetData={datasetData}
              />
            </div>
          )}
        </>
      ) : (
        <Preview
          onBack={() => {
            setSelectedDataset(null);
            setIsPreview(false);
            setIsButtonPreviewEnabled(false);
          }}
          isMultipleSearchTableDataset={isMultipleSearchTableDataset}
          selectedDatasetId={activeSelectedDataset?.id ?? null}
          dataSource={dataSource}
        />
      )}
    </Modal>
  );
};

export default DatasetExplorerPopup;
